/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';

import Master from '../Master';
import IziToast from '../IziToast';
import InfoPanel from '../Panels/InfoPanel';
import Button from '../Buttons';

const NOTIFICATION_TYPES = [
  { key: 'invoice', label: 'Invoice' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'payment_correction', label: 'Payment Correction' },
  { key: 'suspense', label: 'Suspense' },
  { key: 'service_charge', label: 'Service Charge' },
];

const CHANNELS = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

// Build a fully-true matrix (default when a property has nothing configured).
const allOn = () => {
  const m = {};
  NOTIFICATION_TYPES.forEach((t) => {
    m[t.key] = {};
    CHANNELS.forEach((c) => { m[t.key][c.key] = true; });
  });
  return m;
};

// Merge whatever's stored on the property with defaults so the UI always
// renders the full grid even if the saved object is partial.
const hydrateMatrix = (saved) => {
  const base = allOn();
  if (!saved || typeof saved !== 'object') return base;
  NOTIFICATION_TYPES.forEach((t) => {
    const row = saved[t.key];
    if (row && typeof row === 'object') {
      CHANNELS.forEach((c) => {
        if (row[c.key] === false) base[t.key][c.key] = false;
        else if (row[c.key] === true) base[t.key][c.key] = true;
      });
    }
  });
  return base;
};

const ChannelSettings = ({ user }) => {
  const userToken = user ? user.token : '';
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };

  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [matrix, setMatrix] = useState(allOn());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');

  // ── Channel health state ───────────────────────────────────────────────
  // Holds the result of /notifications/health. Lets the admin see whether
  // the SMTP / Africa's Talking / WaSender creds and sessions are actually
  // alive — independent of any property — so a rotated password or
  // disconnected QR is caught BEFORE customers stop receiving messages.
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');
  // Per-channel test-send UI state. `testOpen` tracks which channel's
  // input row is expanded, `testRecipient` is the typed recipient,
  // `testBusy` shows the spinner while a send is in flight.
  const [testOpen, setTestOpen] = useState('');         // '' | 'email' | 'sms' | 'whatsapp'
  const [testRecipient, setTestRecipient] = useState('');
  const [testBusy, setTestBusy] = useState('');         // matches testOpen when in flight

  const isAdmin = user && user.type === 'admin';

  const loadHealth = async () => {
    setHealthLoading(true);
    setHealthError('');
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}notifications/health`,
        requestConfig,
      );
      setHealth(response);
    } catch (e) {
      setHealthError(
        (e.response && e.response.data && e.response.data.error)
        || 'Could not fetch channel health',
      );
    }
    setHealthLoading(false);
  };

  // Fire an actual delivery via the selected channel and merge the
  // result into the health state. The result replaces the probe result
  // for that channel — green = the test arrived (or was accepted by the
  // upstream); red = the actual delivery attempt failed, with the
  // upstream's reason in `detail`.
  const runTest = async (channel) => {
    if (!testRecipient.trim()) {
      IziToast('Enter a recipient first');
      return;
    }
    setTestBusy(channel);
    try {
      const { data: { response } } = await axios.post(
        `${process.env.REACT_APP_API_URL}notifications/test`,
        { channel, recipient: testRecipient.trim() },
        requestConfig,
      );
      setHealth((prev) => ({ ...(prev || {}), [channel]: response }));
      IziToast(
        response.ok ? `${channel.toUpperCase()} test accepted` : `${channel.toUpperCase()} test failed`,
        response.ok ? 'green' : 'red',
      );
    } catch (e) {
      IziToast(
        (e.response && e.response.data && e.response.data.error)
        || `Test ${channel} failed`,
        'red',
      );
    }
    setTestBusy('');
  };

  // Fetch once on mount (admin only) and refresh every 5 minutes while
  // the page is open. The Refresh button remains for on-demand checks
  // after fixing a credential. We deliberately avoid focus / visibility
  // listeners — they fire on every tab switch, dev-tools toggle, and OS
  // window blur, which feels like the page is hammering the backend.
  // Failures are non-fatal — the panel renders the error and the next
  // interval / manual Refresh will retry.
  useEffect(() => {
    if (!isAdmin) return undefined;
    loadHealth();
    const FIVE_MIN = 5 * 60 * 1000;
    const interval = setInterval(loadHealth, FIVE_MIN);
    return () => clearInterval(interval);
  }, []);

  // ── Load properties on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) { setIsLoading(false); return undefined; }
    let active = true;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}properties?take=1000&where={"status":"active"}&order={"name":"ASC"}`;
        const { data: { response } } = await axios.get(url, requestConfig);
        if (!active) return;
        const list = Array.isArray(response) ? response : [response];
        setProperties(list);
        if (list.length > 0) {
          setPropertyId(list[0].id);
          setMatrix(hydrateMatrix(
            list[0].other_details && list[0].other_details.channel_settings,
          ));
        }
        setIsLoading(false);
      } catch (e) {
        if (active) {
          IziToast('Could not load properties');
          setIsLoading(false);
        }
      }
    })();
    return () => { active = false; };
  }, []);

  // When the property selection changes, re-hydrate the matrix from that
  // property's stored channel_settings.
  useEffect(() => {
    if (!propertyId) return;
    const p = properties.find((x) => x.id === propertyId);
    if (p) {
      setMatrix(hydrateMatrix(p.other_details && p.other_details.channel_settings));
    }
  }, [propertyId]);

  const filteredProperties = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) => String(p.name || '').toLowerCase().includes(q));
  }, [search, properties]);

  const toggle = (typeKey, channelKey) => {
    setMatrix((prev) => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        [channelKey]: !prev[typeKey][channelKey],
      },
    }));
  };

  const setRow = (typeKey, value) => {
    setMatrix((prev) => ({
      ...prev,
      [typeKey]: CHANNELS.reduce((acc, c) => { acc[c.key] = value; return acc; }, {}),
    }));
  };

  const setColumn = (channelKey, value) => {
    setMatrix((prev) => {
      const next = { ...prev };
      NOTIFICATION_TYPES.forEach((t) => {
        next[t.key] = { ...prev[t.key], [channelKey]: value };
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!propertyId) {
      IziToast('Select a property first');
      return;
    }
    setIsSaving(true);
    try {
      const { data: { response } } = await axios.put(
        `${process.env.REACT_APP_API_URL}properties/channel-settings`,
        { property_id: propertyId, channel_settings: matrix },
        requestConfig,
      );
      // Reflect the just-saved value in our local properties list so a
      // re-select doesn't show stale data.
      setProperties((prev) => prev.map((p) => (
        p.id === propertyId
          ? {
            ...p,
            other_details: {
              ...(p.other_details || {}),
              channel_settings:
                (response && response.channel_settings) || matrix,
            },
          }
          : p
      )));
      IziToast('Channel settings saved', 'green');
    } catch (e) {
      IziToast(
        (e.response && e.response.data && e.response.data.error)
        || 'Failed to save channel settings',
        'red',
      );
    }
    setIsSaving(false);
  };

  const selectedProperty = properties.find((p) => p.id === propertyId);

  if (!isAdmin) {
    return (
      <Master redirectLink="channels" title="Settings & Configuration" source="Channels">
        <InfoPanel message="This page is available to administrators only." hasError />
      </Master>
    );
  }

  // ── Health card sub-render ─────────────────────────────────────────────
  // Three pills — Email / SMS / WhatsApp — each showing the live state of
  // the upstream provider (SMTP auth ok? AT balance? WaSender session?).
  // Sits above the property picker because creds are system-wide, not
  // per-property; if any pill is red, NO property's notifications will go
  // out regardless of how the matrix is configured. Each pill carries an
  // inline "Send test" toggle so the admin can do a real end-to-end
  // delivery to any recipient and have the card update with the result.
  const renderHealthCard = () => {
    const channelDef = [
      {
        key: 'email', label: 'Email', icon: 'fa-envelope',
        placeholder: 'recipient@example.com', inputType: 'email',
      },
      {
        key: 'sms', label: 'SMS', icon: 'fa-mobile',
        placeholder: '+254712345678', inputType: 'tel',
      },
      {
        key: 'whatsapp', label: 'WhatsApp', icon: 'fa-whatsapp',
        placeholder: '+254712345678', inputType: 'tel',
      },
    ];
    const colorFor = (s) => {
      if (!s) return { bg: '#f1f3f5', fg: '#5b6b7c', dot: '#aab3bd', label: 'Unknown' };
      if (!s.configured) return { bg: '#eef2f5', fg: '#5b6b7c', dot: '#aab3bd', label: 'Off' };
      if (s.ok) return { bg: '#e6f4ea', fg: '#1e7e34', dot: '#27ae60', label: 'Healthy' };
      return { bg: '#fdecea', fg: '#a4282d', dot: '#e74c3c', label: 'Problem' };
    };
    const toggleTest = (channelKey) => {
      // Clicking the toggle on an already-open channel collapses it; on
      // another channel, switches focus to it (and keeps the recipient,
      // since admins typically test the same number across channels).
      setTestOpen((prev) => (prev === channelKey ? '' : channelKey));
    };
    return (
      <div style={{
        background: '#fff',
        border: '1px solid #ececec',
        borderRadius: 8,
        padding: 14,
        marginBottom: 14,
      }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', marginBottom: 10, gap: 8,
        }}
        >
          <div style={{
            fontWeight: 600, color: '#2c3e50', fontSize: 14, flex: 1,
          }}
          >
            Channel health
            <span style={{
              fontWeight: 'normal', color: '#7c8896', fontSize: 12, marginLeft: 8,
            }}
            >
              — live status of the upstream notification providers
            </span>
          </div>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={loadHealth}
            disabled={healthLoading}
            title="Re-run health checks"
          >
            <i
              className={`fa fa-refresh${healthLoading ? ' fa-spin' : ''}`}
              style={{ marginRight: 4 }}
            />
            {healthLoading ? 'Checking…' : 'Refresh'}
          </button>
        </div>

        {healthError ? (
          <div style={{
            background: '#fdecea',
            color: '#a4282d',
            padding: '8px 12px',
            borderRadius: 4,
            fontSize: 13,
          }}
          >
            {healthError}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {channelDef.map((c) => {
              const s = health && health[c.key];
              const col = colorFor(s);
              const isOpen = testOpen === c.key;
              const isBusy = testBusy === c.key;
              return (
                <div
                  key={c.key}
                  style={{
                    flex: '1 1 240px',
                    background: col.bg,
                    border: `1px solid ${col.dot}33`,
                    borderRadius: 6,
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  >
                    <i className={`fa ${c.icon}`} style={{ color: col.fg }} />
                    <strong style={{ color: col.fg, fontSize: 13 }}>{c.label}</strong>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: col.dot,
                      marginLeft: 'auto',
                    }}
                    />
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: col.fg,
                    }}
                    >
                      {col.label}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: '#5b6b7c',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    flex: 1,
                  }}
                  >
                    {s ? s.detail : 'Not checked yet'}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {!isOpen ? (
                      <button
                        type="button"
                        onClick={() => toggleTest(c.key)}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${col.fg}55`,
                          color: col.fg,
                          padding: '3px 10px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <i className="fa fa-paper-plane" style={{ marginRight: 4 }} />
                        Send test
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <input
                          type={c.inputType}
                          placeholder={c.placeholder}
                          value={testRecipient}
                          onChange={(e) => setTestRecipient(e.target.value)}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 12,
                            padding: '4px 8px',
                            border: '1px solid #d4dae0',
                            borderRadius: 4,
                            background: '#fff',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => runTest(c.key)}
                          disabled={isBusy}
                          style={{
                            background: col.fg,
                            color: '#fff',
                            border: 'none',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            opacity: isBusy ? 0.7 : 1,
                          }}
                        >
                          {isBusy ? 'Sending…' : 'Send'}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleTest(c.key)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #d4dae0',
                            color: '#5b6b7c',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                          title="Close"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Master redirectLink="channels" title="Settings & Configuration" source="Channels">
      {isLoading ? (
        <InfoPanel message="Loading..." />
      ) : properties.length === 0 ? (
        <InfoPanel message="No active properties found" />
      ) : (
        <>
          {renderHealthCard()}

          {/* Property picker + filter */}
          <div className="row" style={{ marginBottom: 14 }}>
            <div className="col-md-5">
              <label className="control-label" style={{ fontSize: 12 }}>
                Search property
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-7">
              <label className="control-label" style={{ fontSize: 12 }}>
                Property
              </label>
              <select
                className="form-control"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              >
                {filteredProperties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedProperty && (
            <>
              <div style={{
                background: '#f8f9fa',
                border: '1px solid #eee',
                borderRadius: 6,
                padding: '10px 14px',
                marginBottom: 14,
                fontSize: 13,
                color: '#5b6b7c',
              }}
              >
                Choose which channels are used for each notification type on
                {' '}
                <strong style={{ color: '#2c3e50' }}>{selectedProperty.name}</strong>
                . Anything unchecked is suppressed system-wide for that
                notification type on this property. Defaults are all-on.
              </div>

              <div className="table-responsive">
                <table className="table table-bordered" style={{ marginBottom: 14 }}>
                  <thead>
                    <tr>
                      <th style={{ background: '#f5f6f7' }}>Notification Type</th>
                      {CHANNELS.map((c) => (
                        <th
                          key={c.key}
                          style={{
                            background: '#f5f6f7',
                            textAlign: 'center',
                            width: 110,
                          }}
                        >
                          <div>{c.label}</div>
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            <button
                              type="button"
                              className="btn btn-xs btn-default"
                              style={{ padding: '1px 6px', fontSize: 11 }}
                              onClick={() => setColumn(c.key, true)}
                            >
                              All
                            </button>
                            {' '}
                            <button
                              type="button"
                              className="btn btn-xs btn-default"
                              style={{ padding: '1px 6px', fontSize: 11 }}
                              onClick={() => setColumn(c.key, false)}
                            >
                              None
                            </button>
                          </div>
                        </th>
                      ))}
                      <th style={{ background: '#f5f6f7', width: 130, textAlign: 'center' }}>
                        Row
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {NOTIFICATION_TYPES.map((t) => (
                      <tr key={t.key}>
                        <td style={{ fontWeight: 600, color: '#2c3e50' }}>{t.label}</td>
                        {CHANNELS.map((c) => (
                          <td key={c.key} style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!!matrix[t.key][c.key]}
                              onChange={() => toggle(t.key, c.key)}
                              style={{ transform: 'scale(1.2)' }}
                            />
                          </td>
                        ))}
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-xs btn-default"
                            style={{ padding: '1px 6px', fontSize: 11 }}
                            onClick={() => setRow(t.key, true)}
                          >
                            All
                          </button>
                          {' '}
                          <button
                            type="button"
                            className="btn btn-xs btn-default"
                            style={{ padding: '1px 6px', fontSize: 11 }}
                            onClick={() => setRow(t.key, false)}
                          >
                            None
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ textAlign: 'right' }}>
                <Button
                  caption={isSaving ? 'Saving…' : 'Save channel settings'}
                  cssClass="btn-success"
                  onClick={handleSave}
                  type="button"
                  isBlack={false}
                  disabled={isSaving}
                />
              </div>
            </>
          )}
        </>
      )}
    </Master>
  );
};

ChannelSettings.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

ChannelSettings.defaultProps = {
  user: {},
};

const mapStateToProps = (state) => state;

export default connect(mapStateToProps)(ChannelSettings);
